/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo, useRef } from 'react';

import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';

import { MAX_ALLOWED_SECTIONS, MAX_MENU_HEIGHT } from '../../../constants';
import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks/use-universal-create-analytics';
import { withIntlProvider } from '../../../services';
import type { AppDefinedMenuSection, RenderableMenuSectionData } from '../../../types/menuV2';
import ItemWrapper from '../MenuItem/item-wrapper';
import { isNativeRenderableMenuItem } from '../MenuItemV2';
import { hasMoreButton } from '../MenuSectionPrimitive';
import { RenderableMenuSection } from '../RenderableMenuSection';
import { SkeletonItem } from '../SkeletonItem';

import { sectionsToAppMenuConfig } from './utils/configConverters';
import { computeRenderableSections, computeSectionMetadata } from './utils/sectionComputations';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

function MenuContentV2Inner() {
	const { locale } = useIntl();
	const { sections, crossAppCreationConfig, product, subProduct } = useMenuControl();
	const {
		cloudId = '',
		orgId = '',
		accountId = '',
	} = crossAppCreationConfig?.enabled ? crossAppCreationConfig : {};

	// Determine if we're in native-only mode (no cross-app creation)
	const isNativeOnly = !crossAppCreationConfig?.enabled;

	// Skip appMenuConfig generation when in native-only mode
	const appMenuConfig = useMemo(
		() => sectionsToAppMenuConfig(isNativeOnly ? [] : sections),
		[isNativeOnly, sections],
	);

	const { data, isLoading, hasError, fetchUCMenuItems } = useUCMenuItems({
		cloudId,
		orgId,
		product,
		subProduct,
		userId: accountId,
		locale,
		appMenuConfig,
		skipInitialization: isNativeOnly,
	});

	// Fetch menu items when cross-app creation is enabled
	useEffect(() => {
		if (crossAppCreationConfig?.enabled) {
			fetchUCMenuItems();
		}
	}, [crossAppCreationConfig?.enabled, fetchUCMenuItems]);

	const renderableSections = useMemo(
		() =>
			computeRenderableSections({
				sections,
				data,
				hasError,
				isNativeOnly,
			}),
		[sections, data, hasError, isNativeOnly],
	);

	const sectionsToAnalyze = useMemo(
		() => renderableSections.slice(0, MAX_ALLOWED_SECTIONS),
		[renderableSections],
	);

	// Pre-compute section metadata (start indices and total count) to avoid O(n²) complexity
	const sectionMetadata = useMemo(
		() => computeSectionMetadata(sectionsToAnalyze),
		[sectionsToAnalyze],
	);

	// Only show loading state when cross-app creation is enabled
	// This ensures native-only items render immediately when cross-app is disabled
	if (isLoading && crossAppCreationConfig?.enabled) {
		return (
			<MenuGroup spacing="compact" testId="global-create-content-popover-menu-v2-loading">
				<Box xcss={cx(styles.menuWrap)}>
					<ItemWrapper>
						<SkeletonItem testId="loading-menu-content-v2" />
					</ItemWrapper>
				</Box>
			</MenuGroup>
		);
	}

	if (sectionsToAnalyze.length === 0) {
		return null;
	}

	return (
		<MenuGroup
			spacing="compact"
			testId="global-create-content-popover-menu-v2"
			{...(sectionsToAnalyze.some((section) => section.foldable === 'enhanced') &&
			fg('navx_universal_create_overflow_mode')
				? { maxHeight: MAX_MENU_HEIGHT }
				: undefined)}
		>
			<Box xcss={cx(styles.menuWrap)}>
				<AnalyticsTracker sections={sectionsToAnalyze} />
				{sectionsToAnalyze.map((section, sectionIndex) => (
					<RenderableMenuSection
						section={section}
						sectionIndex={sectionIndex}
						totalMenuItemsCount={sectionMetadata.totalCount}
						sectionStartIndex={sectionMetadata.startIndices[sectionIndex]}
						key={`menu-section-${section.type}-${sectionIndex}`}
					/>
				))}
			</Box>
		</MenuGroup>
	);
}

export function AnalyticsTracker({ sections }: { sections: RenderableMenuSectionData[] }) {
	const { fireNewMenuItemsViewed } = useUniversalCreateAnalytics();
	const analyticsFired = useRef(false);

	useEffect(() => {
		if (sections.length === 0 || analyticsFired.current) {
			return;
		}

		if (fg('navx_uc_cross_app_creation_v2_enabled')) {
			analyticsFired.current = true;
			const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');

			// Flatten all items from all sections
			const allItems = sections.flatMap((section) => section.items);

			// Build metadata for each section
			const sectionMetadata = sections.map((section, sectionIndex) => {
				const sectionItems = section.items;

				// Extract all ATIs and types from items in this section
				const sectionItemAtis = sectionItems.map((item) =>
					isNativeRenderableMenuItem(item) ? item.ati.toString() : item.ati || 'unknown',
				);

				const sectionItemTypes = sectionItems.map((item) =>
					isNativeRenderableMenuItem(item) ? 'native' : item.type,
				);

				// Create metadata object for this section
				const baseMetadata = {
					groupHeading: section.heading,
					groupIndex: sectionIndex,
					groupItemCount: section.items.length,
					itemAtis: sectionItemAtis,
					itemTypes: sectionItemTypes,
					showMoreButton: !!hasMoreButton(section.foldable, section.items.length),
				};

				// Add type-specific properties
				if (section.type === 'appDefinedSection') {
					return {
						...baseMetadata,
						type: 'appDefinedSection' as const,
						personalize: (section as AppDefinedMenuSection).personalize ?? 'off',
					};
				} else {
					return {
						...baseMetadata,
						type: 'suggestionSection' as const,
					};
				}
			});

			// Fire analytics event with aggregated data
			fireNewMenuItemsViewed({
				crossAppCreationEnabled,
				totalSections: sections.length,
				totalNativeItems: allItems.filter(isNativeRenderableMenuItem).length,
				totalCrossAppItems: allItems.filter((item) => !isNativeRenderableMenuItem(item)).length,
				sectionMetadata,
			});
		}
	}, [sections, fireNewMenuItemsViewed]);

	return null;
}

const MenuContentV2WithIntl = withIntlProvider(MenuContentV2Inner);

export const MenuContentV2 = () => (
	<ErrorBoundary fallback={null}>
		<MenuContentV2WithIntl />
	</ErrorBoundary>
);
